// packages/front/src/portfolio/currency-switcher.tsx
//
// CurrencySwitcher — dropdown for selecting the display currency of a portfolio.
// displayCurrency is a portfolio-level setting, persisted via PATCH /api/portfolio/portfolios/:id.

import { DISPLAY_CURRENCY, DISPLAY_CURRENCY_SYMBOL, type TDisplayCurrency } from "@signals/shared";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch, qk } from "../_common";
import { useMutation } from "@tanstack/react-query";

interface CurrencySwitcherProps {
  portfolioId: string;
  currentCurrency: string;
  onCurrencyChanged?: (currency: TDisplayCurrency) => void;
}

export function CurrencySwitcher({
  portfolioId,
  currentCurrency,
  onCurrencyChanged,
}: CurrencySwitcherProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (currency: TDisplayCurrency) =>
      apiFetch(`/api/portfolio/portfolios/${portfolioId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ displayCurrency: currency }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.all });
      void queryClient.invalidateQueries({ queryKey: qk.portfolio.portfolios });
      setIsOpen(false);
    },
  });

  const handleChange = (currency: TDisplayCurrency) => {
    updateMutation.mutate(currency);
    onCurrencyChanged?.(currency);
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        className="flex items-center gap-1 rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>
          {DISPLAY_CURRENCY_SYMBOL[DISPLAY_CURRENCY.find((currency) => currency === currentCurrency) ?? "USD"]}
        </span>
        <span>{currentCurrency}</span>
        <span className="text-gray-400">▼</span>
      </button>
      {isOpen && (
        <div className="absolute right-0 z-50 mt-1 rounded border border-gray-200 bg-white text-gray-900 shadow-lg">
          {DISPLAY_CURRENCY.map((currency) => (
            <button
              key={currency}
              type="button"
              className={`flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 hover:text-gray-900 ${
                currency === currentCurrency ? "bg-blue-50 font-medium" : ""
              }`}
              onClick={() => handleChange(currency)}
            >
              <span className="w-4">{DISPLAY_CURRENCY_SYMBOL[currency]}</span>
              <span>{currency}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
